import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { NotificationManager } from "react-notifications";
import useFetch from "../../hooks/fetchMSSQL-hook";

const MAX_EAN_LEN = 30;
const MAX_TEXT_LEN = 255;
const MAX_DESC_LEN = 2000;

export default function RecipeForm({ form, handleClose, onAdded }) {
  const { fetchMssqlApi } = useFetch();
  const show = form === "new";

  const [vats, setVats] = useState([]);
  const [deposits, setDeposits] = useState([]);

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  const [data, setData] = useState({
    EAN: "",
    Code: "",
    Name: "",
    VATCode: "",
    DepositId: "",
    Description: "",
  });

  const setField = (key) => (e) => {
    const v = e?.target?.value ?? "";
    setData((p) => ({ ...p, [key]: v }));
  };

  const setEAN = (e) => {
    const value = (e.target.value || "").replace(/\D/g, "").slice(0, MAX_EAN_LEN);
    setData((p) => ({ ...p, EAN: value }));
  };

  const clearImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onPickImage = (e) => {
    const file = e.target.files?.[0] || null;

    if (imagePreview) URL.revokeObjectURL(imagePreview);

    if (!file) {
      setImageFile(null);
      setImagePreview(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    if (file.type !== "image/png") {
      NotificationManager.error("Tylko PNG", "Błąd", 2500);
      e.target.value = "";
      setImageFile(null);
      setImagePreview(null);
      return;
    }

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    e.target.value = "";
  };

  useEffect(() => () => { if (imagePreview) URL.revokeObjectURL(imagePreview); }, [imagePreview]);

  useEffect(() => {
    if (!show) return;

    setData({
      EAN: "",
      Code: "",
      Name: "",
      VATCode: "",
      DepositId: "",
      Description: "",
    });
    clearImage();

    fetchMssqlApi(
      "vats",
      { method: "POST", showLoader: false, hideNotification: true, data: { isActive: 1 } },
      (res) => setVats(res || [])
    );

    fetchMssqlApi(
      "deposits",
      { method: "POST", showLoader: false, hideNotification: true, data: { isActive: 1 } },
      (res) => setDeposits(res || [])
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const errors = useMemo(() => {
    const e = {};

    if (!data.EAN) e.EAN = "EAN jest wymagany";
    else if (!/^\d+$/.test(data.EAN)) e.EAN = "EAN tylko cyfry";
    else if (data.EAN.length > MAX_EAN_LEN) e.EAN = `Max ${MAX_EAN_LEN} znaków`;

    if (!data.Code.trim()) e.Code = "Kod dodatkowy jest wymagany";
    else if (data.Code.length > MAX_TEXT_LEN) e.Code = `Max ${MAX_TEXT_LEN} znaków`;

    if (!data.Name.trim()) e.Name = "Nazwa jest wymagana";
    else if (data.Name.length > MAX_TEXT_LEN) e.Name = `Max ${MAX_TEXT_LEN} znaków`;

    if (!data.VATCode) e.VATCode = "Wybierz VAT";

    if (data.Description && data.Description.length > MAX_DESC_LEN) {
      e.Description = `Max ${MAX_DESC_LEN} znaków`;
    }

    return e;
  }, [data]);

  const canSubmit = Object.keys(errors).length === 0;

  const submit = (e) => {
    e.preventDefault();

    if (!canSubmit) {
      NotificationManager.error("Popraw pola zaznaczone na czerwono", "Błąd", 2500);
      return;
    }

    const payload = {
      EAN: Number(data.EAN),
      Code: (data.Code || "").trim() || (data.Name || "").trim(),
      Name: data.Name.trim(),
      VATCode: data.VATCode,
      Description: data.Description || "",
      DepositId: data.DepositId ? Number(data.DepositId) : null, // opcjonalnie
    };

    let dataToSend = payload;

    if (imageFile) {
      const fd = new FormData();
      Object.entries(payload).forEach(([k, v]) => {
        if (v === null || v === undefined) return;
        fd.append(k, String(v));
      });
      fd.append("Image", imageFile);
      dataToSend = fd;
    }

    fetchMssqlApi("recipe",{ method: "POST", hideNotification: true, data: dataToSend },
      (res) => {
        NotificationManager.success("Receptura dodana", "Sukces", 2500);
        if (typeof onAdded === "function") onAdded(res);
        if (typeof handleClose === "function") handleClose();
      },
      (err2) => {
        const msg =
          err2?.response?.data?.message ||
          err2?.data?.message ||
          err2?.message ||
          "Nie udało się dodać receptury";
        NotificationManager.error(msg, "Błąd", 3000);
      }
    );
  };

  return (
    <Modal show={show} onHide={handleClose} backdrop="static" centered>
      <Form onSubmit={submit} id="recipe-form" autoComplete="off">
        <Modal.Header closeButton>
          <Modal.Title>Dodaj recepturę</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>EAN</Form.Label>
            <Form.Control
              className={errors.EAN ? "is-invalid" : ""}
              value={data.EAN}
              onChange={setEAN}
              autoFocus
            />
            {errors.EAN && <div className="invalid-feedback">{errors.EAN}</div>}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Kod dodatkowy</Form.Label>
            <Form.Control
              className={errors.Code ? "is-invalid" : ""}
              value={data.Code}
              onChange={setField("Code")}
              maxLength={MAX_TEXT_LEN}
            />
            {errors.Code && <div className="invalid-feedback">{errors.Code}</div>}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Nazwa</Form.Label>
            <Form.Control
              className={errors.Name ? "is-invalid" : ""}
              value={data.Name}
              onChange={setField("Name")}
              maxLength={MAX_TEXT_LEN}
            />
            {errors.Name && <div className="invalid-feedback">{errors.Name}</div>}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Stawka VAT</Form.Label>
            <Form.Control
              as="select"
              className={errors.VATCode ? "is-invalid" : ""}
              value={data.VATCode}
              onChange={setField("VATCode")}
            >
              <option value="">Wybierz</option>
              {vats.map((v) => (
                <option key={v.Code ?? v.VATId} value={v.Code}>
                  {v.VATName != null ? `${v.VATName}%` : v.Name || "VAT"}
                </option>
              ))}
            </Form.Control>
            {errors.VATCode && <div className="invalid-feedback">{errors.VATCode}</div>}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Kaucja</Form.Label>
            <Form.Control as="select" value={data.DepositId} onChange={setField("DepositId")}>
              <option value="">Brak</option>
              {deposits.map((d) => (
                <option key={d.DepositId} value={d.DepositId}>
                  {d.Name} ({d.Price} zł)
                </option>
              ))}
            </Form.Control>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="d-block">Obraz (PNG)</Form.Label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png"
              style={{ display: "none" }}
              onChange={onPickImage}
            />

            <div className="border rounded d-flex align-items-center w-100" style={{ padding: 12, background: "#fff", gap: 12 }}>
              <div className="border rounded overflow-hidden d-flex align-items-center justify-content-center" style={{ width: 88, height: 88, background: "#f8f9fa", flex: "0 0 88px" }}>
                {imagePreview ? (
                  <img src={imagePreview} alt="Podgląd" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <div className="text-muted d-flex flex-column align-items-center justify-content-center" style={{ lineHeight: 1 }}>
                    <i className="far fa-image" style={{ fontSize: 26, marginBottom: 6 }} />
                    <div style={{ fontSize: 11 }}>Brak</div>
                  </div>
                )}
              </div>

              <div className="d-flex flex-column w-100" style={{ minWidth: 0 }}>
                <div className="d-flex align-items-center w-100" style={{ gap: 10 }}>
                  <div className="w-100" style={{ minWidth: 0 }}>
                    <div className="font-weight-bold" style={{ fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {imageFile?.name ? imageFile.name : "Nie wybrano pliku"}
                    </div>
                    <div className="text-muted" style={{ fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      PNG • rekomendowane 512×512
                    </div>
                  </div>

                  <div className="d-flex align-items-center" style={{ gap: 8, flex: "0 0 auto" }}>
                    <button type="button" className="btn btn-sm btn-primary" onClick={() => fileInputRef.current?.click()}>
                      <i className="fas fa-plus mr-2" />Dodaj
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={clearImage} disabled={!imagePreview}>
                      <i className="fas fa-trash mr-2" />Usuń
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label>Opis</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              className={errors.Description ? "is-invalid" : ""}
              value={data.Description}
              onChange={setField("Description")}
              maxLength={MAX_DESC_LEN}
            />
            {errors.Description && <div className="invalid-feedback">{errors.Description}</div>}
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="light" onClick={handleClose} type="button">
            Anuluj
          </Button>
          <Button variant="primary" type="submit" form="recipe-form" disabled={!canSubmit}>
            Dodaj
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}